/**
 * Statistics and formatting helpers used by the assistant's tools and by
 * `local-answer.ts`.
 *
 * The demo declared `monthOf` and `slope` immediately above `TOOLS`
 * (source lines 3964–3965) because the assistant block was the only consumer.
 * In this port both already exist in `../oem-engine.ts` — the engine ported
 * them once, for the whole app — so this module **re-exports** rather than
 * redeclaring. Nothing here duplicates an engine derivation.
 *
 * What is new here is the small set of shapes-into-strings helpers the demo
 * wrote inline inside `localAnswer` (`$fmt`, the `A$…` interpolations) and the
 * two ranking primitives the tools share.
 *
 * Engine layer (layer 2 of the 4-layer architecture) — pure, no React, no
 * side effects, no clock, no randomness.
 *
 * @see src/versions/v1/shared/oem-engine.ts — where `slope` and `monthOf` live
 * @see docs/logic/workspace/chat-rail.md — the OEM+ local engine section
 */

// Demo line 3964–3965. Re-exported so the assistant reads as one module.
export { fmt$, fmtk, groupNum, monthOf, slope } from '../oem-engine.ts';

import { groupNum } from '../oem-engine.ts';

/**
 * The demo's `const $fmt = n => 'A$' + Math.round(n).toLocaleString()`, declared
 * inside `localAnswer` at source line 4067.
 *
 * Identical output to the engine's `fmt$`; kept under its own name because the
 * ported branches read `${$fmt(x)}` and the two spellings sit side by side in
 * the original.
 */
export const money = (n: number): string => `A$${groupNum(Math.round(n))}`;

/**
 * Count occurrences of `key` across `rows`, rank them, and render the demo's
 * `"<value>: <count>"` strings.
 *
 * Demo line 3974 — `breakdown_trend`'s inner `top(k)` helper. `'(none)'` is the
 * demo's placeholder for a null field and the cut at eight is its slice.
 */
export function topCounts<T>(rows: readonly T[], key: keyof T, limit = 8): string[] {
  const by: Record<string, number> = {};
  rows.forEach((r) => {
    const v = (r[key] as unknown as string) || '(none)';
    by[v] = (by[v] ?? 0) + 1;
  });
  return Object.entries(by)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([n, c]) => `${n}: ${c}`);
}

/** Mean of a numeric series, or `null` when it is empty. */
export const mean = (vals: readonly number[]): number | null =>
  vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;

/** `+x.toFixed(d)` — the demo's round-trip through a fixed-precision string. */
export const fixed = (n: number, d: number): number => +n.toFixed(d);
